/**
 * Normalizes a name string according to specific capitalization rules.
 * - Trims leading/trailing whitespace.
 * - Reduces multiple spaces to single spaces.
 * - Converts the name to lowercase initially.
 * - Capitalizes the first letter of the string.
 * - Capitalizes any letter immediately following a space, hyphen, or apostrophe.
 * - Correctly handles accented characters during capitalization.
 *
 * @param name The input name string.
 * @returns The normalized name string, or an empty string if the input is null, undefined, or empty.
 *
 * @example
 * normalizeName("   jEAn-pAUl   d'ArTAgnan  "); // Returns "Jean-Paul D'Artagnan"
 * normalizeName("élodie");                     // Returns "Élodie"
 * normalizeName("  pierre   ");                // Returns "Pierre"
 * normalizeName("o'malley-smith");            // Returns "O'Malley-Smith"
 * normalizeName("");                           // Returns ""
 * normalizeName(null);                         // Returns ""
 */
export function normalizeName(name: string | null | undefined): string {
    // 1. Handle null, undefined, or empty input
    if (!name) {
        return '';
    }

    // 2. Trim leading/trailing whitespace and reduce multiple spaces to single spaces
    let normalized = name.trim().replace(/\s+/g, ' ');

    // 3. Convert the entire string to lowercase first to simplify capitalization
    //    This ensures consistent handling regardless of initial casing.
    normalized = normalized.toLocaleLowerCase();

    // 4. Capitalize the first letter and any letter following a space, hyphen, or apostrophe.
    //    The regex `(^.|(?<=[-\'\s]).)` means:
    //    - `^.` : Match the first character of the string.
    //    - `|` : OR
    //    - `(?<=[-\'\s]).` : Match any character (`.`) that is preceded by (`?<=`)
    //                         either a hyphen (`-`), an apostrophe (`'`), or a whitespace character (`\s`).
    //    The `g` flag ensures all occurrences are replaced.
    //    The callback `(char) => char.toUpperCase()` converts the matched character to uppercase.
    //    JavaScript/TypeScript's `toUpperCase()` generally handles common accented characters correctly
    //    (e.g., 'é' becomes 'É', 'à' becomes 'À').
    normalized = normalized.replace(/(^.|(?<=[-'\s]).)/g, char =>
        char.toUpperCase()
    );

    return normalized;
}
